import { supabase } from './supabase';
import { SupportedLanguage } from '../i18n';

type StorageLike = {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
};

const getStorage = (): StorageLike => {
  if (typeof globalThis !== 'undefined') {
    const maybeStorage = (globalThis as { localStorage?: StorageLike }).localStorage;
    if (maybeStorage && typeof maybeStorage.getItem === 'function' && typeof maybeStorage.setItem === 'function') {
      return maybeStorage;
    }
  }

  const store = new Map<string, string>();
  return {
    getItem: (key: string) => (store.has(key) ? (store.get(key) as string) : null),
    setItem: (key: string, value: string) => {
      store.set(key, value);
    },
  };
};

interface SignUpWithEmailOptions {
  email: string;
  password: string;
  username?: string;
  language?: SupportedLanguage;
  region?: string;
  referralCode?: string;
}

interface ResetPasswordOptions {
  email: string;
  language?: SupportedLanguage;
}

export class LocalizedAuthService {
  private static storage = getStorage();
  private static readonly LANGUAGE_KEY = 'language';
  private static getWebOrigin(): string {
    try {
      if (typeof window !== 'undefined' && window.location && window.location.origin) {
        return window.location.origin;
      }
    } catch {}
    return 'https://cubemeet.app';
  }

  private static async getCurrentLanguage(): Promise<SupportedLanguage> {
    try {
      const language = this.storage.getItem(this.LANGUAGE_KEY) as SupportedLanguage | null;
      return language || 'zh-CN';
    } catch (error) {
      console.warn('Failed to read stored language preference:', error);
      return 'zh-CN';
    }
  }

  private static async saveUserLanguagePreference(language: SupportedLanguage) {
    try {
      this.storage.setItem(this.LANGUAGE_KEY, language);
      console.log(`✅ Web language preference saved: ${language}`);
    } catch (error) {
      console.error('Failed to persist language preference:', error);
    }
  }

  static async signUpWithLocalizedEmail(options: SignUpWithEmailOptions) {
    const { email, password, username, language, region, referralCode } = options;
    const userLanguage = language || (await this.getCurrentLanguage());

    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            username,
            display_name: username,
            language_preference: userLanguage,
            email_language: userLanguage,
            region,
            referral_code_input: referralCode,
          },
          emailRedirectTo: `https://cubemeet.app/auth/verify?lang=${userLanguage}&redirect=app`,
        },
      });

      if (error) {
        console.error('❌ Web signup failed:', error);
        throw error;
      }

      if (!data?.user) {
        throw new Error('Registration failed: no user returned');
      }

      await this.saveUserLanguagePreference(userLanguage);

      return {
        data,
        userLanguage,
        needsEmailVerification: true,
        email,
        useStandardVerification: true,
      };
    } catch (error) {
      console.error('Localized signup failed (web):', error);
      throw error;
    }
  }

  static async resetPasswordWithLocalizedEmail(options: ResetPasswordOptions) {
    const { email, language } = options;
    const userLanguage = language || (await this.getCurrentLanguage());

    try {
      // For web, use current origin (supports subdomains like travel.cubemeet.app)
      const base = this.getWebOrigin();
      const redirectUrl = `${base}/auth/reset-password?lang=${userLanguage}`;
      const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: redirectUrl,
      });

      if (error) {
        console.error('❌ Password reset failed:', error);
        throw error;
      }

      return { data, userLanguage };
    } catch (error) {
      console.error('Reset password failed (web):', error);
      throw error;
    }
  }

  static async verifyEmail(token: string, type: 'signup' | 'recovery' = 'signup') {
    try {
      const { data, error } = await supabase.auth.verifyOtp({
        token_hash: token,
        type: type === 'signup' ? 'email' : 'recovery',
      });

      if (error) {
        console.error('❌ Email verification failed:', error);
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Email verification failed (web):', error);
      throw error;
    }
  }

  static async resendConfirmation(email: string, language?: SupportedLanguage) {
    const userLanguage = language || (await this.getCurrentLanguage());

    try {
      const { data, error } = await supabase.auth.resend({
        type: 'signup',
        email,
        options: {
          emailRedirectTo: `https://cubemeet.app/auth/verify?lang=${userLanguage}&redirect=app`,
        },
      });

      if (error) {
        console.error('❌ Resend confirmation failed:', error);
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Resend confirmation failed (web):', error);
      throw error;
    }
  }

  static async signInWithEmail(email: string, password: string, language?: SupportedLanguage) {
    const userLanguage = language || (await this.getCurrentLanguage());

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        console.error('❌ Sign in failed:', error);
        throw error;
      }

      await this.saveUserLanguagePreference(userLanguage);

      return { data, userLanguage };
    } catch (error) {
      console.error('Sign in failed (web):', error);
      throw error;
    }
  }

  static async signOut() {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) {
        throw error;
      }
    } catch (error) {
      console.error('Sign out failed (web):', error);
      throw error;
    }
  }
}
